import { useState } from "react";
import { AlertCircle, Check, Loader2, Send, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import type { ResultadoDoConviteEmLote } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

const TETO = 25;

/** Aceita colar de qualquer lugar: uma por linha, separado por vírgula, ponto-e-vírgula ou espaço —
 *  e-mail copiado de planilha ou do campo "Para:" de um e-mail vem em todos esses formatos. */
export function separarEmails(texto: string): string[] {
  return [...new Set(texto.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
}

const EH_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RESUMO: Record<ResultadoDoConviteEmLote["situacao"], { rotulo: string; cor: string; Icone: typeof Check }> = {
  enviado: { rotulo: "Convite enviado", cor: "text-emerald-700 dark:text-emerald-400", Icone: Check },
  ja_membro: { rotulo: "Já está na equipe", cor: "text-zinc-500 dark:text-muted-foreground", Icone: UserCheck },
  ja_convidado: { rotulo: "Já tinha convite pendente", cor: "text-amber-700 dark:text-amber-400", Icone: UserPlus },
  erro: { rotulo: "Não foi possível", cor: "text-red-700 dark:text-red-400", Icone: AlertCircle },
};

type Props = {
  papel: { kind: "base"; role: string } | { kind: "custom"; customRoleId: string; label: string };
  rotuloDoPapel: string;
  onFechar: () => void;
};

/**
 * Convidar mais de um de uma vez.
 *
 * Fica dentro do mesmo diálogo do convite avulso, atrás de um link — quem convida uma pessoa (o caso
 * comum) não paga nada por isso existir, e quem está montando a equipe inteira não precisa repetir o
 * fluxo vinte vezes.
 *
 * O papel é o mesmo pra todo o lote: papel por linha viraria sintaxe que ninguém acerta. Depois de
 * enviar, a tela mostra o que aconteceu com CADA endereço — sobretudo os que foram pulados por já
 * estarem na equipe, que é o resultado mais comum ao colar a lista inteira da clínica.
 */
export function ConviteEmLoteForm({ papel, rotuloDoPapel, onFechar }: Props) {
  const utils = trpc.useUtils();
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState<ResultadoDoConviteEmLote[] | null>(null);

  const emails = separarEmails(texto);
  const invalidos = emails.filter((e) => !EH_EMAIL.test(e));
  const validos = emails.filter((e) => EH_EMAIL.test(e));
  const excedeu = validos.length > TETO;

  const enviar = trpc.team.invite.createMany.useMutation({
    onSuccess: (r) => {
      setResultados(r.resultados);
      utils.team.invite.list.invalidate();
      utils.team.list.invalidate();
      if (r.enviados > 0) toast.success(`${r.enviados} ${r.enviados === 1 ? "convite enviado" : "convites enviados"}.`);
      else toast.info("Nenhum convite novo — ver o detalhamento.");
    },
    onError: (e) => toast.error(e.message || "Erro ao enviar os convites."),
  });

  if (resultados) {
    const porSituacao = (s: ResultadoDoConviteEmLote["situacao"]) => resultados.filter((r) => r.situacao === s);
    return (
      <div className="space-y-3">
        <div className="max-h-72 space-y-3 overflow-y-auto">
          {(["enviado", "ja_membro", "ja_convidado", "erro"] as const).map((situacao) => {
            const itens = porSituacao(situacao);
            if (itens.length === 0) return null;
            const { rotulo, cor, Icone } = RESUMO[situacao];
            return (
              <div key={situacao}>
                <p className={cn("mb-1 flex items-center gap-1.5 text-[12px] font-semibold", cor)}>
                  <Icone className="h-3.5 w-3.5" />
                  {rotulo} ({itens.length})
                </p>
                <ul className="space-y-0.5 pl-5">
                  {itens.map((r) => (
                    <li key={r.email} className="truncate text-[12px] text-zinc-600 dark:text-muted-foreground">
                      {r.email}
                      {r.detalhe && <span className="text-zinc-400"> — {r.detalhe}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={() => {
              setResultados(null);
              setTexto("");
            }}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Convidar mais
          </button>
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-9 items-center rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            Concluir
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          E-mails
        </label>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={5}
          autoFocus
          placeholder={"ana@clinica.com\nbruno@clinica.com\ncarla@clinica.com"}
          className="w-full resize-y rounded-lg border border-zinc-200 bg-white p-3 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground"
        />
        <p className="mt-1 text-[11.5px] text-zinc-500 dark:text-muted-foreground">
          Um por linha, ou separados por vírgula. Todos entram como <strong>{rotuloDoPapel}</strong> — para papéis
          diferentes, envie um lote de cada.
        </p>
      </div>

      {validos.length > 0 && (
        <p className={cn("text-[12px]", excedeu ? "text-red-600 dark:text-red-400" : "text-zinc-600 dark:text-muted-foreground")}>
          {validos.length} {validos.length === 1 ? "endereço válido" : "endereços válidos"}
          {excedeu && ` — o limite é ${TETO} por vez`}
        </p>
      )}
      {invalidos.length > 0 && (
        <p className="text-[12px] text-amber-700 dark:text-amber-400">
          {invalidos.length} sem cara de e-mail e {invalidos.length === 1 ? "será ignorado" : "serão ignorados"}:{" "}
          {invalidos.slice(0, 4).join(", ")}
          {invalidos.length > 4 && "..."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onFechar}
          className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
        >
          Voltar
        </button>
        <button
          type="button"
          disabled={enviar.isPending || validos.length === 0 || excedeu}
          onClick={() =>
            enviar.mutate({
              emails: validos,
              ...(papel.kind === "custom" ? { customRoleId: papel.customRoleId } : { role: papel.role as any }),
              expiresInDays: 7,
            })
          }
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
        >
          {enviar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {validos.length > 1 ? `Enviar ${validos.length} convites` : "Enviar convite"}
        </button>
      </div>
    </div>
  );
}
